import React, { useState, Component } from 'react';
import { CheckCircle2, ClipboardList } from 'lucide-react';
export interface TaskMeta {
  assignee?: string;
  dueDate?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
  status?: 'Open' | 'In Progress' | 'Complete';
  linkedEntity?: string;
  isOverdue?: boolean;
}
export interface TimelineEventProps {
  /** Event title */
  title: string;
  /** Event description */
  description: string;
  /** Event date/time string */
  date: string;
  /** Optional author name */
  author?: string;
  /** Icon component to display (any Lucide icon) */
  icon: ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  /** Hex color for the icon */
  iconColor: string;
  /** Whether this is the last event (no connector line) */
  isLast?: boolean;
  /** Event type identifier */
  eventType?: string;
  /** Task-specific metadata */
  taskMeta?: TaskMeta;
  /** Optional data-id for component identification */
  'data-id'?: string;
}
const priorityColors: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  Low: {
    bg: 'bg-[#D1FAE5]',
    text: 'text-[#065F46]',
    border: 'border-[#A7F3D0]'
  },
  Medium: {
    bg: 'bg-[#FEF3C7]',
    text: 'text-[#92400E]',
    border: 'border-[#FDE68A]'
  },
  High: {
    bg: 'bg-[#FFEDD5]',
    text: 'text-[#9A3412]',
    border: 'border-[#FED7AA]'
  },
  Urgent: {
    bg: 'bg-[#FEE2E2]',
    text: 'text-[#991B1B]',
    border: 'border-[#FECACA]'
  }
};
const statusColors: Record<string, {
  bg: string;
  text: string;
}> = {
  Open: {
    bg: 'bg-[#DBEAFE]',
    text: 'text-[#1E40AF]'
  },
  'In Progress': {
    bg: 'bg-[#EDE9FE]',
    text: 'text-[#5B21B6]'
  },
  Complete: {
    bg: 'bg-[#D1FAE5]',
    text: 'text-[#065F46]'
  }
};
export function TimelineEvent({
  title,
  description,
  date,
  author,
  icon: Icon,
  iconColor,
  isLast = false,
  eventType,
  taskMeta,
  'data-id': dataId
}: TimelineEventProps) {
  const [expanded, setExpanded] = useState(false);
  const isTask = eventType === 'task' && taskMeta;
  const isCompleted = isTask && taskMeta?.status === 'Complete';
  const isOverdue = isTask && taskMeta?.isOverdue;
  // For completed tasks, use a green checkmark icon
  const TaskIcon = isCompleted ? CheckCircle2 : ClipboardList;
  const taskIconColor = isCompleted ? '#10B981' : '#8B5CF6';
  if (isTask && taskMeta) {
    const priority = taskMeta.priority || 'Medium';
    const pColors = priorityColors[priority] || priorityColors.Medium;
    const status = taskMeta.status || 'Open';
    const sColors = statusColors[status] || statusColors.Open;
    return <li className="relative" data-id="element-5467" data-mp="sy2ofwbi3uwe">
        {!isLast && <div className="absolute left-4 top-10 bottom-0 w-px bg-[#E2E8F0]" aria-hidden="true" data-id="element-5468" data-mp="szdrtv11b0mz" />}
        <div className={`flex gap-4 cursor-pointer group rounded-lg -mx-2 px-2 py-1 transition-colors hover:bg-[#F8FAFC] ${isCompleted ? 'opacity-70' : ''}`} onClick={() => setExpanded(!expanded)} role="button" tabIndex={0} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded);
      }} aria-expanded={expanded} aria-label={`Task: ${title}`} data-id="element-5469" data-mp="xxytzzzujue2">
          {/* Icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{
          backgroundColor: `${taskIconColor}15`
        }} aria-hidden="true" data-id="element-5470" data-mp="2xd5zlz04rjr9">
            <TaskIcon className="w-4 h-4" style={{
            color: taskIconColor
          }} data-id="element-5471" data-mp="36edsg1jvnsdy" />
          </div>

          {/* Content */}
          <div className="flex-1 pb-6" data-id="element-5472" data-mp="3g603nxw7trgp">
            <div className="flex items-start justify-between gap-4" data-id="element-5473" data-mp="3g4yr1ozjwwwv">
              <div className="flex-1" data-id="element-5474" data-mp="3u5jckswup6u2">
                {/* Title */}
                <div className={`font-medium text-[14px] ${isCompleted ? 'line-through text-[#94A3B8]' : 'text-[#0F172A]'}`} data-id="element-5475" data-mp="3q3oa3l36onep">
                  {title}
                </div>

                {/* Task metadata row */}
                <div className="flex flex-wrap items-center gap-2 mt-1.5" data-id="element-5476" data-mp="1vq55xsene6kf">
                  {/* Priority badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border ${pColors.bg} ${pColors.text} ${pColors.border}`} data-id="element-5477" data-mp="1x5r35cd3ljz7">
                    {priority}
                  </span>

                  {/* Status badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${sColors.bg} ${sColors.text}`} data-id="element-5478" data-mp="2b6lbxw425mye">
                    {status}
                  </span>

                  {/* Assignee */}
                  {taskMeta.assignee && <span className="text-[12px] text-[#64748B]" data-id="element-5479" data-mp="1iifsdcoqq405">
                      → {taskMeta.assignee}
                    </span>}

                  {/* Due date */}
                  {taskMeta.dueDate && <span className={`text-[12px] font-medium ${isOverdue ? 'text-[#EF4444]' : isCompleted ? 'text-[#94A3B8]' : 'text-[#64748B]'}`} data-id="element-5480" data-mp="1s8hfo299rie0">
                      {isCompleted ? `Completed ${taskMeta.dueDate}` : isOverdue ? 'Overdue' : `Due: ${taskMeta.dueDate}`}
                    </span>}
                </div>
              </div>
              <time className="text-[12px] text-[#94A3B8] whitespace-nowrap" data-id="element-5481" data-mp="s5jgd6vcgeel">
                {date}
              </time>
            </div>

            {/* Expanded detail panel */}
            {expanded && <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-[13px] space-y-2" data-id="element-5482" data-mp="ibsqbxjkjvws">
                <div className="flex justify-between" data-id="element-5483" data-mp="991e3vivcw43">
                  <span className="text-[#64748B]" data-id="element-5484" data-mp="99t8x44n6ptb">Assignee</span>
                  <span className="font-medium text-[#0F172A]" data-id="element-5485" data-mp="4ht4aydmn8qe">
                    {taskMeta.assignee || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-5486" data-mp="3vmjilr2kduuf">
                  <span className="text-[#64748B]" data-id="element-5487" data-mp="3qqkmtiewpajz">Priority</span>
                  <span className={`font-medium ${pColors.text}`} data-id="element-5488" data-mp="3mnxz9tp9qbsl">
                    {priority}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-5489" data-mp="3hq5zkdidtldu">
                  <span className="text-[#64748B]" data-id="element-5490" data-mp="381h610wmw5of">Due Date</span>
                  <span className={`font-medium ${isOverdue ? 'text-[#EF4444]' : 'text-[#0F172A]'}`} data-id="element-5491" data-mp="37zxpd972fsm4">
                    {taskMeta.dueDate || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-5492" data-mp="2y4w10fkm6ukl">
                  <span className="text-[#64748B]" data-id="element-5493" data-mp="331mhv548tu72">Status</span>
                  <span className={`font-medium ${sColors.text}`} data-id="element-5494" data-mp="333z59xsrswri">
                    {status}
                  </span>
                </div>
                {taskMeta.linkedEntity && <div className="flex justify-between" data-id="element-5495" data-mp="3vqa699kagzou">
                    <span className="text-[#64748B]" data-id="element-5496" data-mp="3h2fblnmq0l94">Linked To</span>
                    <span className="font-medium text-[#0EA5E9]" data-id="element-5497" data-mp="3h02wdi8ckozr">
                      {taskMeta.linkedEntity}
                    </span>
                  </div>}
                {!isCompleted && <div className="pt-2 border-t border-[#E2E8F0] flex gap-2" data-id="element-5498" data-mp="djvt3ctugtri">
                    <button className="h-7 px-3 bg-[#10B981] text-white rounded text-[12px] font-medium hover:bg-[#059669] transition-colors" data-id="element-5499" data-mp="icoym2x5064n">
                      Mark Complete
                    </button>
                    <button className="h-7 px-3 bg-white border border-[#CBD5E1] text-[#475569] rounded text-[12px] font-medium hover:bg-[#F1F5F9] transition-colors" data-id="element-5500" data-mp="1y2puyoctdc8a">
                      Edit Task
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </li>;
  }
  // Default non-task rendering
  return <li className="relative" data-id="element-5501" data-mp="1f4daw7kd6hxx">
      {/* Timeline connector line */}
      {!isLast && <div className="absolute left-4 top-10 bottom-0 w-px bg-[#E2E8F0]" aria-hidden="true" data-id="element-5502" data-mp="1f6g5l4wgoz8z" />}
      <div className="flex gap-4" data-id="element-5503" data-mp="1t21ulc2p3j1e">
        {/* Icon */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{
        backgroundColor: `${iconColor}15`
      }} aria-hidden="true" data-id="element-5504" data-mp="1o40aqoxg3fov">
          <Icon className="w-4 h-4" style={{
          color: iconColor
        }} data-id="element-5505" data-mp="2zkgs4nsk86ed" />
        </div>
        {/* Content */}
        <div className="flex-1 pb-6" data-id="element-5506" data-mp="3jne40sju4sb3">
          <div className="flex items-start justify-between gap-4" data-id="element-5507" data-mp="3jk8e5b3dtmeb">
            <div className="flex-1" data-id="element-5508" data-mp="3om7iooauypcu">
              <div className="font-medium text-[14px] text-[#0F172A]" data-id="element-5509" data-mp="3ojbguovw3gj4">
                {title}
              </div>
              <div className="text-[14px] text-[#475569] mt-1" data-id="element-5510" data-mp="18bxfvxremcy">
                {description}
              </div>
              {author && <div className="text-[12px] text-[#94A3B8] mt-1" data-id="element-5511" data-mp="6880nsymu9rg">
                  by {author}
                </div>}
            </div>
            <time className="text-[12px] text-[#94A3B8] whitespace-nowrap" data-id="element-5512" data-mp="b4ygqgaft5cv">
              {date}
            </time>
          </div>
        </div>
      </div>
    </li>;
}